import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Lock as LockIcon,
  PlayArrow as TakeIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import TsdPageBar, { TsdCabinetSelect } from '../components/tsd/TsdPageBar';
import ServiceLabelActions from '../components/fulfillment/ServiceLabelActions';
import useFulfillmentCabinet from '../hooks/useFulfillmentCabinet';
import usePrinter from '../hooks/usePrinter';
import {
  fulfillmentApiBase,
  labelingJobPath,
  labelingJobsPath,
  labelingModeInfo,
  normalizeLabelingJob,
  transferMoveErrors,
  withAccountBody,
  withAccountParams,
} from '../utils/fulfillmentApi.mjs';
import { marketplaceChipLabel } from './fulfillmentUi.mjs';

const REFRESH_MS = 15000;

const FILTERS = [
  { key: 'active', label: 'Активные' },
  { key: 'mine', label: 'Мои' },
];

function errorText(error) {
  if (!error?.response && (error?.code === 'ECONNABORTED' || error?.message === 'Network Error')) {
    return 'Нет связи с сервером. Проверьте Wi-Fi и повторите';
  }
  return error?.response?.data?.message || error?.message || 'Не удалось выполнить операцию';
}

function formatTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

export default function FulfillmentLabelingJobs() {
  const navigate = useNavigate();
  const location = useLocation();
  const printer = usePrinter();
  const printerUid = printer.selectedPrinter?.printer_uid || '';
  const {
    accounts,
    accountsLoading,
    accountsError,
    accountId,
    setAccountId,
    marketplace,
  } = useFulfillmentCabinet();

  const [filter, setFilter] = useState('active');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(location.state?.notice || '');
  const [takingId, setTakingId] = useState(null);
  const [busyJob, setBusyJob] = useState(null);
  const sessionChecked = useRef(false);

  useEffect(() => {
    if (sessionChecked.current) return;
    sessionChecked.current = true;
    // С экрана задания по «К списку» владелец приходит сюда намеренно — не возвращаем его назад.
    if (location.state?.skipSessionRedirect) return;
    (async () => {
      try {
        const { data } = await axios.get(`${fulfillmentApiBase()}/labeling/my-session`, { silent: true });
        if (data?.job && (data.job.job_id ?? data.job.id) != null) {
          navigate(labelingJobPath(data.job), { replace: true });
        }
      } catch {
        /* нет сессии или эндпоинт недоступен — показываем список */
      }
    })();
  }, [location.state, navigate]);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!accountId) {
      setJobs([]);
      return;
    }
    if (!quiet) setLoading(true);
    try {
      const { data } = await axios.get(labelingJobsPath(), {
        params: withAccountParams(marketplace, accountId, { filter }),
        silent: true,
      });
      setJobs((data?.items || []).map(normalizeLabelingJob).filter(Boolean));
      setError('');
    } catch (requestError) {
      if (!quiet) setError(errorText(requestError));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [accountId, marketplace, filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load({ quiet: true });
    }, REFRESH_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') load({ quiet: true });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [load]);

  const take = async (job) => {
    if (takingId) return;
    if (job.labeling.mine) {
      navigate(labelingJobPath(job, accountId));
      return;
    }
    setTakingId(job.id);
    setError('');
    try {
      await axios.post(
        labelingJobsPath(job.id, 'take'),
        withAccountBody(job.marketplace || marketplace, job.account_id ?? accountId, {}),
        { silent: true },
      );
      navigate(labelingJobPath(job, accountId));
    } catch (requestError) {
      const data = requestError?.response?.data || {};
      if (data.code === 'labeling_user_busy') {
        setBusyJob(data.details || {});
      } else {
        setError(errorText(requestError));
        if (data.code === 'labeling_locked') load({ quiet: true });
      }
    } finally {
      setTakingId(null);
    }
  };

  const busyJobText = () => {
    if (!busyJob) return '';
    const mp = marketplaceChipLabel(busyJob.marketplace);
    const account = accounts.find((item) => String(item.id) === String(busyJob.account_id));
    const cabinet = account?.name || account?.organization || (busyJob.account_id ? `ID ${busyJob.account_id}` : '');
    const where = [mp, cabinet ? `кабинет ${cabinet}` : null].filter(Boolean).join(', ');
    return `У вас уже взято задание ${busyJob.code || ''}${where ? ` (${where})` : ''}. Сначала завершите его или выйдите из него.`;
  };

  return (
    <>
      <TsdPageBar
        title="Проклейка"
        extra={(
          <>
            <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
              <TsdCabinetSelect
                id="labeling-account"
                accounts={accounts}
                accountId={accountId}
                disabled={accountsLoading}
                onChange={setAccountId}
              />
            </Box>
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => load()}
              disabled={loading || accountsLoading || !accountId}
              sx={{ minHeight: 40 }}
            >
              Обновить
            </Button>
            <ServiceLabelActions printerUid={printerUid} />
          </>
        )}
      />
      <Stack spacing={2} sx={{ maxWidth: 900, mx: 'auto', pb: 4, width: '100%', minWidth: 0 }}>
        {(error || accountsError) && (
          <Alert severity="error" onClose={() => setError('')}>{error || accountsError}</Alert>
        )}
        {notice && <Alert severity="info" onClose={() => setNotice('')}>{notice}</Alert>}
        {!accountsLoading && !accounts.length && (
          <Alert severity="warning">Нет доступных кабинетов</Alert>
        )}

        <ToggleButtonGroup
          exclusive
          fullWidth
          value={filter}
          onChange={(_event, value) => { if (value) setFilter(value); }}
        >
          {FILTERS.map((item) => (
            <ToggleButton key={item.key} value={item.key} sx={{ minHeight: 48 }}>
              {item.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {(loading || accountsLoading) && <LinearProgress />}
        {!loading && accountId && !jobs.length && (
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="bodyLarge">
              {filter === 'mine' ? 'У вас нет взятых заданий' : 'Заданий на проклейку нет'}
            </Typography>
            <Typography variant="bodySmall" color="text.secondary">
              Задание появится, когда сборщик передаст собранное на проклейку
            </Typography>
          </Paper>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 1.5, minWidth: 0 }}>
          {jobs.map((job) => {
            const { progress, labeling } = job;
            const total = progress.total || progress.transferred;
            const percent = total ? Math.round((progress.labeled / total) * 100) : 0;
            const lockedByOther = Boolean(labeling.locked_by) && !labeling.mine;
            const moveErrors = transferMoveErrors(job.transfers);
            const taking = takingId === job.id;
            const modeInfo = labelingModeInfo(job.labeling_mode);
            return (
              <Card key={`${job.marketplace}-${job.id}`} variant="outlined" sx={{ minWidth: 0 }}>
                <CardContent>
                  <Stack spacing={1.25} sx={{ minWidth: 0 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="titleMedium" sx={{ wordBreak: 'break-word' }}>
                        {job.code || job.name || `Задание ${job.id}`}
                      </Typography>
                      {job.code && job.name && (
                        <Typography variant="bodySmall" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                          {job.name}
                        </Typography>
                      )}
                    </Box>
                    <LinearProgress variant="determinate" value={percent} sx={{ height: 8, borderRadius: 4 }} />
                    <Typography variant="bodyMedium">
                      передано {progress.transferred} / проклеено {progress.labeled} / всего {progress.total}
                    </Typography>
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                      <Chip size="small" variant="outlined" color={modeInfo.color} label={modeInfo.label} />
                      {progress.in_hands > 0 && (
                        <Chip
                          size="small"
                          color="primary"
                          label={`${modeInfo.value === 'during_picking' ? 'проклеено' : 'в руках'} ${progress.in_hands}`}
                        />
                      )}
                      {progress.remaining > 0 && (
                        <Chip size="small" variant="outlined" label={`осталось ${progress.remaining}`} />
                      )}
                      {moveErrors.map((transfer) => (
                        <Tooltip key={transfer.id} title={transfer.ms_move_error || ''}>
                          <Chip
                            size="small"
                            color="error"
                            label={`МС: ошибка${transfer.seq_no != null ? ` (партия №${transfer.seq_no})` : ''}`}
                          />
                        </Tooltip>
                      ))}
                      {job.pallets_open.map((pallet) => (
                        <Chip
                          key={pallet.id}
                          size="small"
                          variant="outlined"
                          label={`${pallet.code || `#${pallet.id}`} · ${Number(pallet.items_count) || 0} шт`}
                        />
                      ))}
                    </Stack>
                    {labeling.locked_by && (
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <LockIcon fontSize="small" color={labeling.mine ? 'primary' : 'disabled'} />
                        <Typography variant="bodySmall" color="text.secondary">
                          {labeling.mine ? 'Взято вами' : `Взял: ${labeling.locked_by_name || 'другой сотрудник'}`}
                          {labeling.taken_at ? ` ${formatTime(labeling.taken_at)}` : ''}
                        </Typography>
                      </Stack>
                    )}
                    <Button
                      variant="contained"
                      size="large"
                      color={labeling.mine ? 'success' : 'primary'}
                      startIcon={taking ? <CircularProgress size={18} color="inherit" /> : <TakeIcon />}
                      onClick={() => take(job)}
                      disabled={Boolean(takingId) || lockedByOther || (!labeling.mine && !labeling.can_take)}
                      sx={{ minHeight: 52 }}
                    >
                      {labeling.mine ? 'Продолжить' : (lockedByOther ? 'Занято' : 'Взять в работу')}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Stack>

      <Dialog open={Boolean(busyJob)} onClose={() => setBusyJob(null)} fullWidth maxWidth="xs">
        <DialogTitle>Уже есть задание в работе</DialogTitle>
        <DialogContent>
          <Typography>{busyJobText()}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBusyJob(null)} size="large" sx={{ minHeight: 52, flex: 1 }}>
            Отмена
          </Button>
          <Button
            variant="contained"
            size="large"
            sx={{ minHeight: 52, flex: 1 }}
            disabled={busyJob?.job_id == null}
            onClick={() => {
              const target = busyJob;
              setBusyJob(null);
              navigate(labelingJobPath(target, accountId));
            }}
          >
            Перейти
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
